import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {loadIsocrona} from '../Common/Isocrona'
import {AllGeoJSON, FeatureCollection} from '@turf/turf'
import {FilterContext} from '../../context/FilterContext'
import {encodeJsonIntersect, getLayer} from '../MapLayers/Layer'
import {MapboxLayer} from './MapBox/MapBox'
import {sleep} from '../../../explorer/components/common'

export function Mapas({data, setMapImageExport}: {data: any; setMapImageExport: any}) {
  const {geometriaSelecionada, endereco} = useContext(FilterContext)
  const [isocronas, setIsocronas] = useState<FeatureCollection>(null)
  const [layers, setLayers] = useState(null)
  const [mapIndex, setMapIndex] = useState(0)
  const mapIndexRef = useRef(0)
  const mountedRef = useRef(true)
  const mapImagesRef = useRef({
    map1: null,
    map2: null,
    map3: null,
    concorrencia: [],
  })
  const processingRef = useRef(false)
  const refLegenda = useRef<HTMLDivElement>(null)
  const geometria = geometriaSelecionada?.geometry
  const geojson = {
    type: 'FeatureCollection',
    features: [],
  }

  useEffect(() => {
    let isMounted = true // controle de montagem
    if (isMounted) {
      loadIsocrona({lat: endereco.latitude, lng: endereco.longitude}, (data) => {
        const geojson: FeatureCollection = {
          type: 'FeatureCollection',
          features: data.features,
        }

        setIsocronas(geojson)
      })
    }
    return () => {
      isMounted = false // Cleanup quando o componente for desmontado
    }
  }, [endereco])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    mapIndexRef.current = mapIndex
  }, [mapIndex])

  useEffect(() => {
    //eslint-disable-next-line array-callback-return
    data?.map((obj, i) => {
      if (obj.computed) {
        geojson.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [obj.geom.coordinates[0], obj.geom.coordinates[1]],
          },
          properties: {},
        })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const mapsList = useMemo(() => {
    if (!geometriaSelecionada) {
      return []
    }
    let json = geometria as AllGeoJSON
    const jsonEncoded = encodeJsonIntersect(json)

    const baseLayerConfigRenda = getLayer('view_ibge_explorer_renda_mensal_domiciliar')
    const layerConfigRenda = {
      ...baseLayerConfigRenda,
      config: {...baseLayerConfigRenda.config},
    }
    const baseTileRenda =
      typeof layerConfigRenda.config.tile === 'string'
        ? `${layerConfigRenda.config.tile.split('?')[0]}?`
        : ''
    let intersect = `${baseTileRenda}layer=${layerConfigRenda.name}_operacao&l1=${layerConfigRenda.name}&s=${layerConfigRenda.schema}&l2=&g1=${jsonEncoded}`
    layerConfigRenda.config.tile = intersect

    /**LAyer Lancamentos */
    const geojsonLancamentos = {
      type: 'FeatureCollection',
      features: [],
    }
    data?.sort(function (a, b) {
      return a.distancia - b.distancia
    })

    let count = 1
    //eslint-disable-next-line array-callback-return
    data?.map((obj, i) => {
      if (obj.computed && !obj.removed) {
        if (count <= 15) {
          geojsonLancamentos.features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [obj.geom.coordinates[0], obj.geom.coordinates[1]],
            },
            properties: {
              id: i + 1,
              nome: `${count} - ${obj.nome}`,
              distance: obj.distance,
            },
          })
        }
        count++
      }
    })

    /** VENDA **/
    const baseLayerConfigVenda = getLayer('view_explorer_apartamento_venda_')
    const layerConfigVenda = {
      ...baseLayerConfigVenda,
      config: {...baseLayerConfigVenda.config},
    }
    const baseTileVenda =
      typeof layerConfigVenda.config.tile === 'string'
        ? `${layerConfigVenda.config.tile.split('?')[0]}?`
        : ''
    intersect = `${baseTileVenda}layer=${layerConfigVenda.name}_operacao&l1=${layerConfigVenda.name}&s=${layerConfigVenda.schema}&l2=&g1=${jsonEncoded}`
    layerConfigVenda.config.tile = intersect

    const createBgLayer = (id: string, data: any) => ({
      id,
      data,
      paint: {
        'circle-color': '#ff0000',
        'circle-radius': 12,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#9f0202',
        'circle-opacity': 1,
      },
      paintText: {},
      type: 'geojson',
      layout: {},
      typeLayer: 'circle',
      visibility: 'visible',
    })

    const createLabelLayer = (id: string, data: any) => ({
      id,
      data,
      paint: {
        'text-color': '#ffffff',
        // 'text-halo-color': '#ffffff',
        'text-halo-width': 3,
      },
      paintText: {
        'text-color': '#000000',
      },
      type: 'geojson',
      layout: {
        'text-field': ['to-string', ['get', 'id']],
        'text-size': 14,
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-offset': [0, -0.7],
        'text-anchor': 'top',
        'text-allow-overlap': true,
        'icon-allow-overlap': true,
        'text-ignore-placement': true,
      },
      typeLayer: 'symbol',
      visibility: 'visible',
    })
    const createLabelLayerText = (id: string, data: any) => ({
      id,
      data,
      paint: {
        'text-color': '#000000',
        'text-halo-color': '#ffffff',
        'text-halo-width': 3,
      },
      paintText: {
        'text-color': '#000000',
      },
      type: 'geojson',
      layout: {
        'text-field': ['to-string', ['get', 'nome']],
        'text-size': 14,
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-offset': [0, -0.7],
        'text-anchor': 'top',
        'text-allow-overlap': true,
        'icon-allow-overlap': true,
        'text-ignore-placement': true,
      },
      typeLayer: 'symbol',
      visibility: 'visible',
    })


    console.log('layerConfigRenda.style')
    console.log(layerConfigRenda.style)

    layerConfigRenda.style ={
      'fill-color': [
        'step',
        ['to-number', ['get', 'renda_mensal_domiciliar']],
        '#FF6666',   // < 1000
        1000, '#FFB266',   // 1000 - 2499
        2500, '#FFFF66',   // 2500 - 4999
        5000, '#B2FF66',   // 5000 - 7499
        7500, '#66FF66',   // 7500 - 8999
        9000, '#66FFFF',   // 9000 - 14999
        15000, '#66B2FF',  // 15000 - 19999
        20000, '#6666FF'   // >= 20000
      ],
      'fill-opacity': 0.8,
    }

    const rendaLayer = {
      id: 'renda',
      tiles: [layerConfigRenda.config.tile],
      source: 'view_ibge_explorer_renda_mensal_domiciliar_operacao',
      paint: layerConfigRenda.style,
      layout: {},
      paintText: {
        'text-color': '#000000',
      },
      type: 'vector',
      typeLayer: 'fill',
      legandaTitle: 'Renda',
      visibility: 'visible',
    }
    console.log('rendaLayer')
    console.log(rendaLayer)
    const lancamentosLayer = createLabelLayer('lancamentos', geojsonLancamentos)
    const lancamentosBgLayer = createBgLayer('lancamentos_bg', geojsonLancamentos)

    const vendaLayer = {
      id: 'venda',
      tiles: [layerConfigVenda.config.tile],
      source: 'view_explorer_apartamento_venda__operacao',
      paint: {
        'circle-color': [
          'interpolate',
          ['linear'],
          ['to-number', ['get', 'preco_m2']],
          2000,
          '#FF3333',
          4000,
          '#FF3333',
          6000,
          '#FF9933',
          8000,
          '#FFFF33',
          10000,
          '#99FF33',
          12000,
          '#33FF33',
          14000,
          '#33FF99',
          16000,
          '#33FFFF',
          18000,
          '#3399FF',
        ],
        'circle-radius': 5,
        'circle-opacity': 1,
      },
      layout: {},
      paintText: {
        'text-color': '#000000',
      },
      type: 'vector',
      typeLayer: 'circle',
      legandaTitle: 'Preco por m2',
      visibility: 'visible',
    }

    const maps = [
      {id: 'renda', key: 'map1', layers: [rendaLayer], concIndex: null},
      {
        id: 'lancamentos',
        key: 'map2',
        layers: [lancamentosBgLayer, lancamentosLayer],
        concIndex: null,
      },
      {id: 'venda', key: 'map3', layers: [vendaLayer], concIndex: null},
    ]

    const comparaveis = []

    data?.forEach((item) => {
      item.tipologias?.forEach((tipologia) => {
        if (tipologia.comparar) {
          comparaveis.push({item})
        }
      })
    })

    comparaveis.forEach((comparavel, index) => {
      const obj = comparavel.item
      if (!obj?.geom?.coordinates) {
        return
      }
      const geojsonConcorrencia = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [obj.geom.coordinates[0], obj.geom.coordinates[1]],
            },
            properties: {
              id: index + 1,
              nome: obj.nome,
            },
          },
        ],
      }

      maps.push({
        id: `concorrencia_${index}`,
        key: 'concorrencia',
        concIndex: index,
        layers: [
          // createBgLayer(`lancamentos_concorrencia_bg_${index}`, geojsonConcorrencia),
          createLabelLayerText(`lancamentos_concorrencia_${index}`, geojsonConcorrencia),
        ],
      })
    })

    return maps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, geometria, geometriaSelecionada])

  useEffect(() => {
    mapIndexRef.current = 0
    setMapIndex(0)

    mapImagesRef.current = {map1: null, map2: null, map3: null, concorrencia: []}
    if (mountedRef.current) {
      setLayers(mapsList[0]?.layers || null)
      setMapImageExport(mapImagesRef.current)
    }
  }, [mapsList, setMapImageExport])

  if (!geometriaSelecionada) {
    return <></>
  }

  async function onload({map}: {map: any}) {
    if (processingRef.current) {
      return
    }
    const currentIndex = mapIndexRef.current
    const currentMap = mapsList[currentIndex]
    if (!currentMap) {
      return
    }
    const currentImages = mapImagesRef.current
    if (currentMap.key === 'concorrencia') {
      if (currentImages.concorrencia?.[currentMap.concIndex]) {
        return
      }
    } else if (currentImages[currentMap.key]) {
      return
    }
    processingRef.current = true
    await sleep(2000)
    const canvas = map?.target?.getCanvas?.()
    const mapImageCanvas = canvas?.toDataURL?.('image/png', 1.0)
    if (!mapImageCanvas || !layers) {
      processingRef.current = false
      return
    }
    if (!mountedRef.current) {
      processingRef.current = false
      return
    }
    const nextImages = {...mapImagesRef.current}
    if (currentMap.key === 'concorrencia') {
      const nextConcorrencia = Array.isArray(nextImages.concorrencia)
        ? [...nextImages.concorrencia]
        : []
      nextConcorrencia[currentMap.concIndex] = mapImageCanvas
      nextImages.concorrencia = nextConcorrencia
    } else {
      nextImages[currentMap.key] = mapImageCanvas
    }
    mapImagesRef.current = nextImages
    if (mountedRef.current) {
      setMapImageExport(nextImages)
    }
    const nextIndex = currentIndex + 1
    if (nextIndex < mapsList.length) {
      mapIndexRef.current = nextIndex
      if (mountedRef.current) {
        setMapIndex(nextIndex)
        setLayers(mapsList[nextIndex]?.layers || null)
      }
    }
    processingRef.current = false
  }

  const currentMap = mapsList[mapIndex]

  return (
    <div style={{position: 'fixed', width: '850', height: '850', left: '-1000px', top: 0}}>
      {/* <div style={{position: 'relative', width: '850', height: '850', left: '100px', top: 0}}> */}

      {layers ? (
        <MapboxLayer
          key={currentMap?.id || mapIndex}
          latLng={[endereco.latitude, endereco.longitude]}
          zoom={14.5}
          onLoad={(e) => {
            onload({map: e})
          }}
          style={{width: 850, height: 650}}
          layers={[
            ...[
              {
                id: 'isocrona',
                data: isocronas,
                paint: {
                  'line-color': [
                    'case',
                    ['==', ['get', 'value'], 300],
                    '#3e6b2e',
                    ['==', ['get', 'value'], 600],
                    '#f10808',
                    '#f10808',
                  ],
                  'line-width': 2,
                  'line-dasharray': [3, 5],
                },
                paintText: {
                  'text-color': '#000000', // Cor do texto
                },
                type: 'geojson',
                layout: {},
                typeLayer: 'line',
                visibility: 'visible',
              },
            ],
            ...layers,
          ]}
          inputRefLeganda={refLegenda}
          areaInfluencia={geometria}
        ></MapboxLayer>
      ) : (
        <></>
      )}

      {/* <Mapa1 data={data} setMapImageExport={setMapaToExport1} isocronas={isocronas} geometria={geometriaSelecionada.geometry} endereco={endereco} ></Mapa1>
      { mapImage.map1 ? <Mapa2 data={data} setMapImageExport={setMapaToExport2} isocronas={isocronas} geometria={geometriaSelecionada.geometry} endereco={endereco}></Mapa2> :<></> }
      { mapImage.map1 && mapImage.map2 ? <Mapa3 data={data} setMapImageExport={setMapaToExport3} isocronas={isocronas} geometria={geometriaSelecionada.geometry} endereco={endereco}></Mapa3> : <></> } */}
    </div>
  )
}
